import type { SectionProps } from "../context";
import { domId } from "../context";
import { ATTENDANCE, AttendanceChoice, FIELD, FormField, FormStatus, Honeypot } from "../forms";
import { Icon } from "../icons";
import { BUTTON, Container, MultiLine, REVEAL, SectionHeader, SectionShell, cx } from "../ui";

const SAMPLES = [
  { name: "Rina & keluarga", message: "Selamat menempuh hidup baru! Semoga menjadi keluarga yang sakinah, mawaddah, warahmah.", attendance: "yes", when: "2 jam lalu" },
  { name: "Budi Santoso", message: "Barakallahu lakuma wa baraka alaikuma. Bahagia selalu, ya!", attendance: "yes", when: "kemarin" },
  { name: "Teman kantor", message: "Maaf belum bisa hadir. Doa terbaik untuk kalian berdua.", attendance: "no", when: "3 hari lalu" },
] as const;

const ATTENDANCE_LABEL: Record<string, string> = Object.fromEntries(ATTENDANCE);

/**
 * Guestbook: a form plus the newest wishes. Live, the responses runtime loads the list from
 * /__baze/wishes into [data-baze-wishes-list] using the hidden prototype item, and posts the form there. The editor
 * shows sample wishes so the design can be judged.
 */
export function Wishes({ node, ctx }: SectionProps<"wishes">) {
  const c = node.content;
  const stream = node.variant === "stream";
  const inert = ctx.editor || ctx.thumbnail;
  const item = cx("rounded-site-md border border-site-border bg-site-surface p-4 shadow-site-sm @md:p-5", stream && "border-0 border-l-2 border-l-site-primary rounded-none bg-transparent shadow-none py-2");
  return (
    <SectionShell ctx={ctx} node={node}>
      <Container size="narrow">
        <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} align="center" />
        <MultiLine ctx={ctx} text={c.description} path="content.description" className="mx-auto mt-4 max-w-xl text-center leading-relaxed text-site-muted" />
        <div className={cx(REVEAL, "mt-10 space-y-10")} data-baze-wishes="">
          <form className="relative space-y-4 rounded-site-lg border border-site-border bg-site-surface p-5 shadow-site-sm @md:p-7" data-baze-wishes-form="" noValidate>
            <FormField ctx={ctx} id={`${node.id}-name`} label="Nama">
              <input id={domId(ctx, `${node.id}-name`)} name="name" required maxLength={60} autoComplete="name" disabled={inert} className={FIELD} placeholder="Nama Anda" />
            </FormField>
            <FormField ctx={ctx} id={`${node.id}-message`} label="Ucapan & doa">
              <textarea id={domId(ctx, `${node.id}-message`)} name="message" required maxLength={500} rows={4} disabled={inert} className={cx(FIELD, "resize-y")} placeholder="Tulis ucapan dan doa terbaik Anda" />
            </FormField>
            {c.askAttendance ? <AttendanceChoice ctx={ctx} name="attendance" idBase={`${node.id}-att`} disabled={inert} /> : null}
            <Honeypot />
            <div className="flex flex-wrap items-center gap-3">
              <button type="submit" disabled={inert} className={cx("baze-btn", BUTTON.primary, "disabled:cursor-not-allowed")}>
                <Icon name="heart" className="size-4" />
                <span>Kirim ucapan</span>
              </button>
              <FormStatus />
            </div>
          </form>

          <div>
            {inert ? (
              <ol className={cx(stream ? "space-y-5" : "grid gap-3 @2xl:grid-cols-2")} data-baze-wishes-list="" aria-label="Ucapan dari tamu">
                {SAMPLES.map((w) => (
                  <li key={w.name} className={item}>
                    <WishBody name={w.name} message={w.message} attendance={ATTENDANCE_LABEL[w.attendance]} when={w.when} />
                  </li>
                ))}
              </ol>
            ) : (
              // Live: the runtime appends wishes here, possibly before React hydrates. dangerouslySetInnerHTML
              // makes React leave the children alone (an unexpected child would regenerate the whole tree).
              <ol className={cx(stream ? "space-y-5" : "grid gap-3 @2xl:grid-cols-2")} data-baze-wishes-list="" aria-label="Ucapan dari tamu" dangerouslySetInnerHTML={{ __html: "" }} />
            )}
            {inert ? null : (
              <>
                <p data-baze-wishes-empty="" hidden className="text-center text-sm text-site-muted">
                  Belum ada ucapan. Jadilah yang pertama!
                </p>
                <div className="mt-6 flex justify-center">
                  <button type="button" data-baze-wishes-more="" hidden className={BUTTON.secondary}>
                    Muat lebih banyak
                  </button>
                </div>
                {/* A hidden prototype, not a <template>: the browser moves a template's children into an inert
                    fragment, so React finds none when it hydrates and regenerates the page, dropping the
                    runtime's form listeners. */}
                <ol hidden aria-hidden="true" data-baze-wish-template="">
                  <li className={item}>
                    <WishBody />
                  </li>
                </ol>
              </>
            )}
          </div>
        </div>
      </Container>
    </SectionShell>
  );
}

/** One wish. Without props it renders the empty slots the runtime fills (textContent only). */
function WishBody({ name, message, attendance, when }: { name?: string; message?: string; attendance?: string; when?: string }) {
  return (
    <>
      <p className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span data-name="" className="font-semibold text-site-text">
          {name}
        </span>
        <span data-attendance="" hidden={!attendance} className="rounded-full bg-site-primary/10 px-2 py-0.5 text-[0.6875rem] font-semibold text-site-primary">
          {attendance}
        </span>
        <span data-time="" className="text-xs text-site-muted">
          {when}
        </span>
      </p>
      <p data-message="" className="mt-2 leading-relaxed break-words whitespace-pre-line text-site-text/90">
        {message}
      </p>
    </>
  );
}
